import { getCompanyDomain } from '@aibees/elephant/components/company-details/utils';
import { CurrentCompanyContactDataPopover } from '@aibees/elephant/components/current-company-contact-data-popover';
import { JobHistory } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/person-job-info/components/job-history';
import { ROUTES } from '@aibees/elephant/router';
import { formatCompanyLocation } from '@aibees/elephant/utils';
import {
  CustomPopover,
  Icon,
  IconType,
  InternalLinkButton,
  Label,
  TextEllipsis,
  usePopover,
} from '@aibees/shared/components';
import { classnames, urlToValidHttps } from '@aibees/shared/utils';
import { Box, Link, Skeleton, Stack, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { useSuggestionSearchBarContext } from '../../context';
import { DetailHeader, type SocialLink } from '../detail-header';
import { DetailInfo } from '../detail-info';
import * as Styled from './styles';
import * as SX from './sx';
import type { PeopleDetailProps as Props } from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

export const PeopleDetail = memo(({ className }: Props) => {
  const { highlightedItem, isOptionsLoading } = useSuggestionSearchBarContext();

  const personData = highlightedItem?.fullData;
  const domainsPopoverState = usePopover();
  const personDataCompanyDomain = getCompanyDomain(personData?.company?.link);

  const currentCompany = useMemo(() => {
    return personData?.company
      ? { company: { ...personData.company, ...personData.company.summary } }
      : personData?.positionGroups?.find((x) => x.current);
  }, [personData]);

  const hasCompany = !isOptionsLoading;

  const companyRanges = useMemo(
    () => highlightedItem?.fullData?.company?.summary?.staff?.range,
    [highlightedItem?.fullData?.company?.summary?.staff?.range]
  );

  const handleOpenDomainPopover = (event: React.MouseEvent<HTMLElement>) => {
    domainsPopoverState.onOpen(event.currentTarget);
  };

  const companyData: {
    icon?: IconType;
    label?: string;
    value?: string | React.ReactElement;
  }[] = useMemo(
    () => [
      {
        icon: 'world',
        label: 'Domain',
        value: (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="body4" color="grey.700" fontWeight={700}>
              {personDataCompanyDomain ? (
                <Link
                  href={urlToValidHttps(personDataCompanyDomain)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="inherit"
                >
                  {personDataCompanyDomain}
                </Link>
              ) : (
                'N/A'
              )}
            </Typography>

            {personData?.company?.link?.domains?.length &&
              personData?.company?.link?.domains?.length > 1 && (
                <Label
                  size="xxsmall"
                  variant="soft"
                  color="primary"
                  onClick={handleOpenDomainPopover}
                >
                  {`+${personData?.company?.link?.domains?.length - 1}`}
                </Label>
              )}
          </Stack>
        ),
      },
      {
        value: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            gap={0.5}
          >
            <Stack alignItems="center" justifyContent="center">
              <Icon name="groups-outlined" color="grey.500" size={18} />
            </Stack>

            <Typography variant="body4" fontWeight={500} color="grey.700">
              Employee Size:
            </Typography>

            <TextEllipsis
              variant="body4"
              color="grey.700"
              fontWeight={700}
              value={
                companyRanges?.start
                  ? `${companyRanges?.start}${
                      companyRanges?.end ? ` - ${companyRanges?.end}` : ''
                    } +`
                  : 'N/A'
              }
            />
          </Stack>
        ),
      },
      {
        value: (
          <DetailInfo
            icon="engineering-outline"
            label="Primary Industry"
            value={personData?.company?.summary?.industry}
          />
        ),
      },
      {
        value: (
          <DetailInfo
            icon="location"
            label="Company Location"
            value={formatCompanyLocation([
              personData?.company?.location?.headquarter?.country,
              personData?.company?.location?.headquarter?.state,
            ])}
          />
        ),
      },
    ],
    [
      personDataCompanyDomain,
      personData?.company?.link?.domains?.length,
      personData?.company?.summary?.industry,
      personData?.company?.location?.headquarter?.country,
      personData?.company?.location?.headquarter?.state,
      handleOpenDomainPopover,
      companyRanges?.start,
      companyRanges?.end,
    ]
  );

  const socialLinks = useMemo((): SocialLink[] => {
    const links: SocialLink[] = [
      {
        href: highlightedItem?.fullData?.url,
        icon: 'linkedin' as const,
      },
      {
        href: highlightedItem?.fullData?.link?.twitter,
        icon: 'twitter' as const,
      },
      {
        href: highlightedItem?.fullData?.link?.facebook,
        icon: 'facebook' as const,
      },
      {
        href: highlightedItem?.fullData?.link?.instagram,
        icon: 'instagram' as const,
      },
    ];

    return links.filter((socialLink) => socialLink.href); // Only show links that have URLs
  }, [highlightedItem]);

  const state = usePopover();
  const stateCompany = usePopover();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorElCompany, setAnchorElCompany] = useState<HTMLElement | null>(
    null
  );
  const allCurrentRoles = useMemo(() => {
    return personData?.positionGroups?.filter((w) => !w.date?.end) || [];
  }, [personData?.positionGroups]);

  const allCurrentRolesInCompanies = useMemo(() => {
    return personData?.positionGroups?.filter((w) => w.current) || [];
  }, [personData?.positionGroups]);

  const totalRolesCount = useMemo(() => {
    return allCurrentRoles.reduce(
      (total, role) => total + (role.profile_positions?.length || 0),
      0
    );
  }, [allCurrentRoles]);
  const totalRolesCountInCompanies = useMemo(() => {
    return allCurrentRolesInCompanies.reduce(
      (total, role) => total + (role.profile_positions?.length || 0),
      0
    );
  }, [allCurrentRolesInCompanies]);

  const handleJobPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    state.onOpen(event.currentTarget);
  };

  const handleCompanyPopoverOpenCompany = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorElCompany(event.currentTarget);
    stateCompany.onOpen(event.currentTarget);
  };

  if (!highlightedItem) {
    return null;
  }

  return (
    <Styled.Wrapper
      className={classnames('PeopleDetail', className)}
      sx={SX.root}
    >
      <Stack>
        <DetailHeader
          name={personData?.name || ''}
          avatarSrc={personData?.logo || ''}
          links={socialLinks}
          id={personData?.id}
          icon="person-outline"
          isPeople={true}
        />

        <Stack gap={1.5}>
          <DetailInfo
            isLoading={isOptionsLoading}
            icon="location"
            label="Contact Location"
            value={personData?.location?.default}
          />

          <DetailInfo
            isLoading={isOptionsLoading}
            icon="portrait"
            label="Job Title"
            value={personData?.title || 'N/A'}
            subData={
              totalRolesCount > 1 && (
                <Label
                  size="xxsmall"
                  color="primary"
                  variant="soft"
                  onClick={handleJobPopoverOpen}
                >
                  {`+${totalRolesCount - 1}`}
                </Label>
              )
            }
          />

          {hasCompany && (
            <Stack gap={0.5}>
              {/* <Typography variant="body3" color="grey.700">
                Current Company:
              </Typography> */}

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                gap={0.5}
              >
                <Box>
                  <Icon name="business" color="grey.500" size={18} />
                </Box>
                <Typography variant="body4" color="grey.700" fontWeight={500}>
                  Company:
                </Typography>

                {currentCompany?.company?.id ? (
                  <InternalLinkButton
                    to={ROUTES.company.replace(
                      ':id',
                      currentCompany.company.id
                    )}
                    fullWidth={false}
                  >
                    <Typography
                      variant="body4"
                      color="grey.700"
                      fontWeight={700}
                    >
                      {currentCompany?.company?.name || 'N/A'}
                    </Typography>
                  </InternalLinkButton>
                ) : (
                  <Typography variant="body4" color="grey.700" fontWeight={700}>
                    {currentCompany?.company?.name || 'N/A'}
                  </Typography>
                )}

                {totalRolesCountInCompanies > 1 && (
                  <Label
                    size="xxsmall"
                    variant="soft"
                    color="primary"
                    onClick={handleCompanyPopoverOpenCompany}
                  >
                    +{totalRolesCountInCompanies - 1}
                  </Label>
                )}
              </Stack>

              <Stack sx={SX.companyInfoWrapper}>
                {companyData.map((item, index) => (
                  <Box key={`${item.label}-${index}`} sx={SX.companyInfo}>
                    <Styled.BranchSvg
                      src={
                        index === 0
                          ? '/assets/images/branch-2.svg'
                          : '/assets/images/branch.svg'
                      }
                    />
                    {item.icon && (
                      <Icon name={item.icon} size={16} color="grey.500" />
                    )}
                    {item.label && (
                      <Typography
                        variant="body4"
                        color="grey.700"
                        fontWeight={400}
                      >
                        {item.label}:{' '}
                      </Typography>
                    )}
                    {typeof item.value === 'string' ? (
                      <TextEllipsis
                        variant="body5"
                        fontWeight="700"
                        color="grey.700"
                        value={item.value || 'N/A'}
                      />
                    ) : (
                      item.value
                    )}
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}

          {isOptionsLoading && (
            <Skeleton animation="wave" variant="rounded" sx={SX.skeleton} />
          )}
        </Stack>
      </Stack>

      <CustomPopover
        state={state}
        anchorEl={anchorEl}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        contentSx={{ overflowY: 'hidden' }}
        disableRestoreFocus
      >
        <JobHistory isCompanySummary={false} currentRoles={allCurrentRoles} />
      </CustomPopover>

      <CustomPopover
        state={stateCompany}
        anchorEl={anchorElCompany}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        contentSx={{ overflowY: 'hidden' }}
        disableRestoreFocus
      >
        <JobHistory
          isCompanySummary={false}
          currentRoles={allCurrentRolesInCompanies}
        />
      </CustomPopover>

      <CurrentCompanyContactDataPopover
        icon="network"
        type="network"
        state={domainsPopoverState}
        value={personData?.company?.link?.domains || []}
        copyValue={personData?.company?.link?.domains?.at(0) || ''}
      />
    </Styled.Wrapper>
  );
});

PeopleDetail.displayName = 'PeopleDetail';
